import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';

export type NightlySweepStatLine = {
  statLineId: string;
  playerId: string;
  playerName: string;
  currentTeam: string | null;
  currentLeague: string | null;
  position: string | null;
  gameDate: string;
  league: string;
  team: string;
  opponent: string | null;
  isHome: number | null;
  score: string | null;
  result: string | null;
  goals: number | null;
  assists: number | null;
  points: number | null;
  plusMinus: number | null;
  pim: number | null;
  shots: number | null;
  hits: number | null;
  blocked: number | null;
  giveaways: number | null;
  takeaways: number | null;
  toiSeconds: number | null;
  decision: string | null;
  goalsAgainst: number | null;
  shotsAgainst: number | null;
  saves: number | null;
  savePct: number | null;
  shutout: number | null;
  flags: Array<{ flagType: string; status: 'ok' | 'warn' | 'critical' }>;
};

export type NightlySweepNote = {
  id: string;
  playerId: string;
  playerName: string;
  currentTeam: string | null;
  currentLeague: string | null;
  type: 'note' | 'viewing' | 'video' | 'schedule';
  title: string;
  body: string | null;
  occurredAt: number;
  createdAt: number;
  createdBy: string | null;
};

export type NightlySweepResponse = {
  date: string;
  noteWindow: { startAt: number; endAt: number; label: string };
  totals: {
    clientsPlayed: number;
    statRows: number;
    leagues: number;
    flaggedRows: number;
    criticalFlags: number;
    noteEntries: number;
  };
  topPerformers: NightlySweepStatLine[];
  flagged: NightlySweepStatLine[];
  goalieLines: NightlySweepStatLine[];
  leagueGroups: Array<{ league: string; count: number; lines: NightlySweepStatLine[] }>;
  notes: NightlySweepNote[];
  email: {
    enabled: boolean;
    configured: boolean;
    recipients: string[];
  };
};

export type NightlySweepSendResult = {
  sent: number;
  skipped: string | null;
  recipients: string[];
  date: string;
};

export function useNightlySweep(date?: string | null) {
  const query = date ? `?date=${date}` : '';
  return useQuery<NightlySweepResponse>({
    queryKey: ['nightly-sweep', date ?? 'latest'],
    queryFn: () => apiGet<NightlySweepResponse>(`/api/nightly-sweep${query}`),
    staleTime: 30_000,
  });
}

export function sendNightlySweepTest(date?: string | null) {
  const query = date ? `?date=${date}` : '';
  return apiPost<NightlySweepSendResult>(`/api/nightly-sweep/send-test${query}`, {});
}
